import { CloudUpload, Loader2 } from "lucide-react";
import { type DragEvent, useCallback, useId, useState } from "react";
import { presignUploadSingleFile } from "@/shared/lib/presign-upload-single-file";

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

function isAllowedPdfOrJpeg(file: File): boolean {
  const t = (file.type || "").toLowerCase();
  if (t === "application/pdf" || t === "image/jpeg" || t === "image/jpg") {
    return true;
  }
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".pdf") || name.endsWith(".jpg") || name.endsWith(".jpeg")
  );
}

export type PresignedFileDropZoneProps = {
  keyPrefix: string;
  onSuccess?: (key: string) => void;
  onError?: (error: Error) => void;
  /** Max file size in bytes (default 10MB) */
  maxBytes?: number;
  title: string;
  subtitle: string;
  selectFilesLabel?: string;
  disabled?: boolean;
  className?: string;
};

export function PresignedFileDropZone({
  keyPrefix,
  onSuccess,
  onError,
  maxBytes = DEFAULT_MAX_BYTES,
  title,
  subtitle,
  selectFilesLabel = "SELECT FILES",
  disabled = false,
  className,
}: PresignedFileDropZoneProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = useCallback(
    async (file: File) => {
      setError(null);
      if (!isAllowedPdfOrJpeg(file)) {
        const msg = "ຮອງຮັບເຉພາະ PDF ຫຼື JPG";
        setError(msg);
        onError?.(new Error(msg));
        return;
      }
      if (file.size > maxBytes) {
        const msg = `ໄຟລ์ໃຫຍ່ເກີນ ${Math.round(maxBytes / (1024 * 1024))} MB`;
        setError(msg);
        onError?.(new Error(msg));
        return;
      }
      setUploading(true);
      try {
        const key = await presignUploadSingleFile(file, keyPrefix);
        onSuccess?.(key);
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err.message);
        onError?.(err);
      } finally {
        setUploading(false);
      }
    },
    [keyPrefix, maxBytes, onSuccess, onError],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void processFile(file);
      e.target.value = "";
    },
    [processFile],
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !uploading) setIsDragging(true);
    },
    [disabled, uploading],
  );

  const onDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled || uploading) return;
      const file = e.dataTransfer.files?.[0];
      if (file) void processFile(file);
    },
    [disabled, uploading, processFile],
  );

  const blocked = disabled || uploading;

  return (
    <div className={className}>
      <input
        id={inputId}
        type="file"
        accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
        className="sr-only"
        disabled={blocked}
        onChange={onInputChange}
      />
      <section
        aria-label="File upload drop zone"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`rounded-lg border-2 border-dashed bg-background transition-colors ${
          isDragging
            ? "border-primary bg-muted/40"
            : "border-muted-foreground/35 hover:border-muted-foreground/55"
        } ${blocked ? "pointer-events-none opacity-60" : ""}`}
      >
        <label
          htmlFor={inputId}
          className={`flex flex-col items-center justify-center gap-3 px-6 py-10 text-center ${
            blocked ? "cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          {uploading ? (
            <Loader2 className="size-10 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <CloudUpload
              className="size-10 shrink-0 text-muted-foreground"
              strokeWidth={1.25}
              aria-hidden
            />
          )}
          <div className="space-y-1">
            <p className="font-semibold text-foreground text-sm">{title}</p>
            <p className="text-muted-foreground text-xs">{subtitle}</p>
          </div>
          <span className="mt-1 inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 font-medium text-foreground text-sm uppercase tracking-wide">
            {uploading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                ກຳລັງອັບໂຫຼດ...
              </>
            ) : (
              selectFilesLabel
            )}
          </span>
        </label>
      </section>
      {error ? <p className="mt-2 text-destructive text-sm">{error}</p> : null}
    </div>
  );
}
